<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import MessageAPI from '../../../../api/inbox/message';
import crmAPI from '../api/crm';
import schedulingAPI from '../../scheduling/api/scheduling';
import { useEmitter } from '../../../../composables/emitter';
import { useConversationTheme } from '../../conversation/themes';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import AudioChip from 'dashboard/components-next/message/chips/Audio.vue';
import WhatsappAlbumGrid from 'dashboard/components-next/message/bubbles/WhatsappAlbumGrid.vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const props = defineProps({
  deal: {
    type: Object,
    default: null,
  },
  stages: {
    type: Array,
    default: () => [],
  },
  accountId: {
    type: [Number, String],
    required: true,
  },
});

const emit = defineEmits(['close', 'deal-updated', 'edit-deal', 'open-schedule-modal']);

const store = useStore();
const { t } = useI18n();
const { themeClass, themeFeatures } = useConversationTheme();

// View Mode: Split View (Chat na Esquerda + Grade de Vagas na Direita)
const isSplitViewOpen = ref(false);

// Resize Drawer State & Persistence (Drag to Resize)
const STORAGE_KEY_DRAWER_WIDTH = 'crm_deal_chat_drawer_width';
const STORAGE_KEY_SPLIT_WIDTH = 'crm_deal_split_drawer_width';
const STORAGE_KEY_CHAT_COLUMN_WIDTH = 'crm_deal_chat_column_width';

const GRID_MIN_WIDTH = 540;
const CHAT_MIN_WIDTH = 380;

const isResizing = ref(false);
const isResizingChatColumn = ref(false);
const isMaximized = ref(false);

const singleDrawerWidth = ref(Number(localStorage.getItem(STORAGE_KEY_DRAWER_WIDTH)) || 540);
const splitDrawerWidth = ref(Number(localStorage.getItem(STORAGE_KEY_SPLIT_WIDTH)) || 1080);
const chatColumnWidth = ref(Number(localStorage.getItem(STORAGE_KEY_CHAT_COLUMN_WIDTH)) || 480);
const preMaximizeWidth = ref(540);

const currentDrawerWidth = computed(() => {
  if (isSplitViewOpen.value) {
    const requiredMin = chatColumnWidth.value + GRID_MIN_WIDTH;
    const baseW = Math.max(splitDrawerWidth.value, requiredMin);
    return isMaximized.value ? window.innerWidth - 30 : Math.min(baseW, window.innerWidth - 20);
  }
  return isMaximized.value ? window.innerWidth - 30 : singleDrawerWidth.value;
});

// Redimensionamento da Borda Externa (Drawer Geral)
const startResize = () => {
  isResizing.value = true;
  document.addEventListener('mousemove', handleResize);
  document.addEventListener('mouseup', stopResize);
  document.body.style.userSelect = 'none';
  document.body.style.cursor = 'col-resize';
};

const handleResize = e => {
  if (!isResizing.value) return;
  const newWidth = window.innerWidth - e.clientX;
  const maxWidth = window.innerWidth - 20;

  if (isSplitViewOpen.value) {
    const minWidth = CHAT_MIN_WIDTH + GRID_MIN_WIDTH;
    if (newWidth >= minWidth && newWidth <= maxWidth) {
      splitDrawerWidth.value = newWidth;
      // Ao diminuir a janela toda, encolhe o chat se necessário para nunca empurrar a grade para fora!
      const availableChat = newWidth - GRID_MIN_WIDTH;
      if (chatColumnWidth.value > availableChat) {
        chatColumnWidth.value = Math.max(CHAT_MIN_WIDTH, availableChat);
      }
      isMaximized.value = false;
    }
  } else {
    const minWidth = CHAT_MIN_WIDTH;
    if (newWidth >= minWidth && newWidth <= maxWidth) {
      singleDrawerWidth.value = newWidth;
      isMaximized.value = false;
    }
  }
};

const stopResize = () => {
  if (!isResizing.value) return;
  isResizing.value = false;
  document.removeEventListener('mousemove', handleResize);
  document.removeEventListener('mouseup', stopResize);
  document.body.style.userSelect = '';
  document.body.style.cursor = '';
  if (isSplitViewOpen.value) {
    localStorage.setItem(STORAGE_KEY_SPLIT_WIDTH, String(splitDrawerWidth.value));
    localStorage.setItem(STORAGE_KEY_CHAT_COLUMN_WIDTH, String(chatColumnWidth.value));
  } else {
    localStorage.setItem(STORAGE_KEY_DRAWER_WIDTH, String(singleDrawerWidth.value));
  }
};

// Redimensionamento do Divisor Interno (Splitter entre WhatsApp e Grade de Vagas)
let startChatResizeX = 0;
let initialChatWidth = 480;

const startChatColumnResize = e => {
  isResizingChatColumn.value = true;
  startChatResizeX = e.clientX;
  initialChatWidth = chatColumnWidth.value;
  document.addEventListener('mousemove', handleChatColumnResize);
  document.addEventListener('mouseup', stopChatColumnResize);
  document.body.style.userSelect = 'none';
  document.body.style.cursor = 'col-resize';
};

const handleChatColumnResize = e => {
  if (!isResizingChatColumn.value) return;
  const delta = e.clientX - startChatResizeX;
  const newWidth = initialChatWidth + delta;
  const maxChatWidth = Math.min(750, window.innerWidth - GRID_MIN_WIDTH - 40);

  if (newWidth >= CHAT_MIN_WIDTH && newWidth <= maxChatWidth) {
    chatColumnWidth.value = newWidth;
    // Garante que a largura total da janela cresça junto se o chat aumentar, para nunca empurrar a grade!
    if (splitDrawerWidth.value < newWidth + GRID_MIN_WIDTH) {
      splitDrawerWidth.value = newWidth + GRID_MIN_WIDTH;
    }
  }
};

const stopChatColumnResize = () => {
  if (!isResizingChatColumn.value) return;
  isResizingChatColumn.value = false;
  document.removeEventListener('mousemove', handleChatColumnResize);
  document.removeEventListener('mouseup', stopChatColumnResize);
  document.body.style.userSelect = '';
  document.body.style.cursor = '';
  localStorage.setItem(STORAGE_KEY_CHAT_COLUMN_WIDTH, String(chatColumnWidth.value));
  localStorage.setItem(STORAGE_KEY_SPLIT_WIDTH, String(splitDrawerWidth.value));
};

const toggleMaximize = () => {
  if (isMaximized.value) {
    if (isSplitViewOpen.value) {
      splitDrawerWidth.value = preMaximizeWidth.value || 1080;
    } else {
      singleDrawerWidth.value = preMaximizeWidth.value || 540;
    }
    isMaximized.value = false;
  } else {
    preMaximizeWidth.value = isSplitViewOpen.value ? splitDrawerWidth.value : singleDrawerWidth.value;
    isMaximized.value = true;
  }
};

const messages = ref([]);
const isLoadingMessages = ref(false);
const messageContent = ref('');
const isPrivateNote = ref(false);
const isSending = ref(false);
const messagesContainer = ref(null);
const textareaRef = ref(null);
const fileInputRef = ref(null);
const selectedFile = ref(null);

const isEditingValue = ref(false);
const tempValue = ref(0);

// Audio Recording State (Oficial do Chatwoot / Zero Delay)
const isRecordingAudio = ref(false);
const hasRecordedAudio = ref(false);
const recordingAudioState = ref('stopped');
const recordingAudioDurationText = ref('00:00');
const audioRecorderInput = ref(null);
const inReplyToMessage = ref(null);

// WhatsApp Style Reactions State
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
const activeReactionMenuMsgId = ref(null);

// Stickers / Figurinhas State (Máximo 20 por Servidor)
const MAX_STICKERS = 20;
const showStickersMenu = ref(false);
const stickers = ref([]);
const isLoadingStickers = ref(false);
const isImportingSticker = ref(false);
const stickerFileInputRef = ref(null);

// Canned Responses / Slash Commands (/) State
const showCannedMenu = ref(false);
const cannedSearchQuery = ref('');
const selectedCannedIndex = ref(0);
const slashTriggerIndex = ref(-1);

const cannedResponses = computed(() => store.getters['getCannedResponses'] || []);
const currentUser = computed(() => store.getters['getCurrentUser'] || {});

// Helpers de Mensagem
const isMessageDeleted = msg => {
  return Boolean(
    msg?.content_attributes?.deleted ||
    msg?.content_attributes?.is_deleted ||
    msg?.contentAttributes?.deleted ||
    msg?.contentAttributes?.is_deleted ||
    msg?.content === 'Mensagem apagada' ||
    msg?.content === 'Esta mensagem foi apagada' ||
    msg?.content === 'Esta mensagem foi excluída' ||
    msg?.content === 'This message was deleted'
  );
};

const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;
  const inReplyToId =
    parentMessage.content_attributes?.in_reply_to ||
    parentMessage.contentAttributes?.inReplyTo ||
    parentMessage.content_attributes?.inReplyTo ||
    parentMessage.contentAttributes?.in_reply_to;

  if (!inReplyToId) return null;
  return messages.value.find(m => m.id === inReplyToId) || null;
};

const getAudioAttachments = msg => {
  if (!msg?.attachments || !Array.isArray(msg.attachments)) return [];
  return msg.attachments.filter(a => {
    const isAudioType =
      a.file_type === 'audio' ||
      a.fileType === 'audio' ||
      a.file_type?.startsWith('audio/') ||
      a.fileType?.startsWith('audio/');
    const url = a.data_url || a.dataUrl || '';
    const hasAudioExt = /\.(ogg|oga|opus|webm|mp3|wav|m4a|aac)(\?.*)?$/i.test(url) || url.includes('/audio/');
    return isAudioType || hasAudioExt;
  });
};

const getImageAttachments = msg => {
  if (!msg?.attachments || !Array.isArray(msg.attachments)) return [];
  const audioList = getAudioAttachments(msg);
  return msg.attachments.filter(a => {
    const url = a.data_url || a.dataUrl;
    if (!url) return false;
    const isImageType =
      a.file_type === 'image' ||
      a.fileType === 'image' ||
      a.file_type?.startsWith('image/') ||
      a.fileType?.startsWith('image/');
    return isImageType && !audioList.includes(a);
  });
};

const isImageOnlyDealMessage = msg => {
  if (!msg) return false;
  const imageAtts = getImageAttachments(msg);
  if (imageAtts.length !== 1) return false;
  const att = imageAtts[0];
  const isSticker = isStickerAttachment(att);
  const hasNoText = !msg.content || msg.content.trim() === '';
  const notPrivate = !msg.private;
  return !isSticker && hasNoText && notPrivate && !isMessageDeleted(msg);
};

const groupedDisplayMessages = computed(() => {
  const result = [];
  const list = messages.value || [];
  let i = 0;

  while (i < list.length) {
    const current = list[i];

    if (!isImageOnlyDealMessage(current)) {
      result.push({ isAlbum: false, message: current });
      i++;
      continue;
    }

    const currentAtt = getImageAttachments(current)[0];
    const albumItems = [{
      id: current.id,
      attachmentId: currentAtt.id,
      url: currentAtt.data_url || currentAtt.dataUrl,
      message: current,
    }];

    let j = i + 1;
    while (j < list.length) {
      const next = list[j];
      if (
        isImageOnlyDealMessage(next) &&
        next.message_type === current.message_type &&
        (next.sender_id === current.sender_id || next.message_type === 0)
      ) {
        const diffSeconds = Math.abs((new Date(next.created_at) - new Date(current.created_at)) / 1000);
        if (diffSeconds <= 180) {
          const nextAtt = getImageAttachments(next)[0];
          albumItems.push({
            id: next.id,
            attachmentId: nextAtt.id,
            url: nextAtt.data_url || nextAtt.dataUrl,
            message: next,
          });
          j++;
          continue;
        }
      }
      break;
    }

    if (albumItems.length >= 2) {
      const lastMsg = albumItems[albumItems.length - 1].message;
      result.push({
        isAlbum: true,
        id: `album_${current.id}_${lastMsg.id}`,
        messageType: current.message_type,
        time: formatTime(lastMsg.created_at),
        status: lastMsg.status,
        items: albumItems,
        primaryMessage: lastMsg,
      });
      i = j;
    } else {
      result.push({ isAlbum: false, message: current });
      i++;
    }
  }

  return result;
});

const openWhatsappContextMenu = (e, msg) => {
  e.preventDefault();
  e.stopPropagation();
  emitter.emit('OPEN_WHATSAPP_CONTEXT_MENU', {
    x: e.pageX || e.clientX,
    y: e.pageY || e.clientY,
    message: {
      id: msg.id,
      content: msg.content,
      contentAttributes: msg.content_attributes || msg.contentAttributes || {},
      content_attributes: msg.content_attributes || msg.contentAttributes || {},
      messageType: msg.message_type,
      message_type: msg.message_type,
      createdAt: msg.created_at || msg.createdAt,
      created_at: msg.created_at || msg.createdAt,
      conversationId: conversationId.value,
      conversation_id: conversationId.value,
      senderType: msg.sender_type || (msg.message_type === 1 ? 'User' : 'Contact'),
      sender: msg.sender,
      attachments: msg.attachments || [],
    },
  });
};

const onReplyToMessage = msg => {
  inReplyToMessage.value = msg;
  nextTick(() => {
    if (textareaRef.value) textareaRef.value.focus();
  });
};

const cancelReply = () => {
  inReplyToMessage.value = null;
};

// Scheduling / Grid State for Split View
const isLoadingGrid = ref(false);
const selectedPeriod = ref('morning');
const selectedProfessionalId = ref('');
const selectedServiceId = ref('');
const gridProfessionals = ref([]);
const gridServices = ref([]);
const gridData = ref({});
const defaultCapacity = ref(1);

const periods = [
  { key: 'morning', label: 'Manhã', icon: '🌅' },
  { key: 'afternoon', label: 'Tarde', icon: '☀️' },
  { key: 'night', label: 'Noite', icon: '🌙' },
  { key: 'all', label: 'Dia Todo', icon: '📋' },
];

const timeSlots = computed(() => {
  const morning = ['06:00', '07:00', '08:00', '09:00', '10:00', '11:00'];
  const afternoon = ['12:00', '13:00', '14:00', '15:00', '16:00', '17:00'];
  const night = ['18:00', '19:00', '20:00', '21:00', '22:00'];

  if (selectedPeriod.value === 'morning') return morning;
  if (selectedPeriod.value === 'afternoon') return afternoon;
  if (selectedPeriod.value === 'night') return night;
  return [...morning, ...afternoon, ...night];
});

const daysOfWeek = [
  { key: 'monday', label: 'SEG', full: 'Segunda-feira' },
  { key: 'tuesday', label: 'TER', full: 'Terça-feira' },
  { key: 'wednesday', label: 'QUA', full: 'Quarta-feira' },
  { key: 'thursday', label: 'QUI', full: 'Quinta-feira' },
  { key: 'friday', label: 'SEX', full: 'Sexta-feira' },
  { key: 'saturday', label: 'SÁB', full: 'Sábado' },
];

const filteredCannedResponses = computed(() => {
  const query = cannedSearchQuery.value.toLowerCase().trim();
  if (!query) return cannedResponses.value.slice(0, 15);

  return cannedResponses.value
    .filter(
      item =>
        (item.short_code && item.short_code.toLowerCase().includes(query)) ||
        (item.content && item.content.toLowerCase().includes(query))
    )
    .slice(0, 15);
});

const hasConversation = computed(() => {
  return !!props.deal?.conversation_id || !!props.deal?.conversation?.id;
});

const conversationId = computed(() => {
  return props.deal?.conversation_id || props.deal?.conversation?.id;
});

const contact = computed(() => {
  return props.deal?.contact || {};
});

const contactName = computed(() => {
  return contact.value?.name || props.deal?.title || t('CRM.DEAL.UNNAMED');
});

const contactIdentifier = computed(() => {
  return contact.value?.phone_number || contact.value?.email || '';
});

const currentStage = computed(() => {
  return props.stages.find(s => s.id === props.deal?.pipeline_stage_id) || props.deal?.pipeline_stage || {};
});

const formattedValue = computed(() => {
  const val = Number(props.deal?.value || 0);
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: props.deal?.currency || 'BRL',
  }).format(val);
});

const formattedRecordingTimer = computed(() => {
  const mins = Math.floor(recordingSeconds.value / 60);
  const secs = recordingSeconds.value % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
});

const scrollToBottom = async () => {
  await nextTick();
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
};

const fetchMessages = async () => {
  if (!conversationId.value) {
    messages.value = [];
    return;
  }

  isLoadingMessages.value = true;
  try {
    const response = await MessageAPI.getPreviousMessages({
      conversationId: conversationId.value,
    });
    const payload = response.data?.payload || response.data || [];
    messages.value = Array.isArray(payload) ? payload : [];
    scrollToBottom();
  } catch (error) {
    console.error('Erro ao carregar mensagens da conversa:', error);
  } finally {
    isLoadingMessages.value = false;
  }
};

const sendMessage = async () => {
  if (
    (!messageContent.value.trim() && !selectedFile.value && !hasRecordedAudio.value) ||
    isSending.value ||
    !conversationId.value
  )
    return;

  const content = messageContent.value.trim();
  const isPrivate = isPrivateNote.value;
  const file = selectedFile.value;

  messageContent.value = '';
  selectedFile.value = null;
  showCannedMenu.value = false;
  isSending.value = true;

  try {
    const payload = {
      conversationId: conversationId.value,
      message: content,
      private: isPrivate,
      echo_id: `temp-${Date.now()}`,
    };

    if (inReplyToMessage.value?.id) {
      payload.contentAttributes = {
        in_reply_to: inReplyToMessage.value.id,
      };
      inReplyToMessage.value = null;
    }

    if (file) {
      payload.files = [file];
      if (file.isVoiceMessage) {
        payload.isVoiceMessage = true;
      }
    }

    await MessageAPI.create(payload);
    hasRecordedAudio.value = false;
    isRecordingAudio.value = false;
    recordingAudioState.value = 'stopped';
    await fetchMessages();
  } catch (error) {
    console.error('Erro ao enviar mensagem:', error);
    messageContent.value = content;
  } finally {
    isSending.value = false;
  }
};

// Handlers Oficiais do AudioRecorder
const onAudioProgressChanged = duration => {
  recordingAudioDurationText.value = duration;
};

const onFinishRecorder = fileData => {
  recordingAudioState.value = 'stopped';
  hasRecordedAudio.value = true;
  if (fileData?.file) {
    const autoRecordedFile = {
      ...fileData.file,
      isVoiceMessage: true,
    };
    selectedFile.value = autoRecordedFile;
  }
};

const onRecordError = () => {
  isRecordingAudio.value = false;
  hasRecordedAudio.value = false;
  recordingAudioState.value = 'stopped';
};

const toggleAudioRecorder = () => {
  isRecordingAudio.value = !isRecordingAudio.value;
  if (!isRecordingAudio.value) {
    hasRecordedAudio.value = false;
    selectedFile.value = null;
    recordingAudioState.value = 'stopped';
  }
};

const toggleAudioRecorderPlayPause = () => {
  if (!audioRecorderInput.value) return;
  if (recordingAudioState.value === 'stopped') {
    audioRecorderInput.value.stopRecording();
    recordingAudioState.value = 'paused';
  } else {
    audioRecorderInput.value.playPause();
  }
};

const cancelAudioRecording = () => {
  if (audioRecorderInput.value) {
    try {
      audioRecorderInput.value.stopRecording();
    } catch {
      /* ignore */
    }
  }
  isRecordingAudio.value = false;
  hasRecordedAudio.value = false;
  selectedFile.value = null;
  recordingAudioState.value = 'stopped';
};

const sendRecordedAudio = async () => {
  if (isSending.value) return;

  if (audioRecorderInput.value) {
    try {
      const audioFile = await audioRecorderInput.value.stopAndGetFile();
      if (audioFile) {
        audioFile.isVoiceMessage = true;
        selectedFile.value = audioFile;
        hasRecordedAudio.value = true;
      }
    } catch (err) {
      console.warn('Erro ao obter áudio gravado:', err);
    }
  }

  await sendMessage();
};

// WhatsApp Reactions Methods
const toggleReactionMenu = msgId => {
  activeReactionMenuMsgId.value = activeReactionMenuMsgId.value === msgId ? null : msgId;
};

const toggleReaction = (msg, emoji) => {
  activeReactionMenuMsgId.value = null;
  if (!msg.content_attributes) {
    msg.content_attributes = {};
  }
  if (!msg.content_attributes.reactions) {
    msg.content_attributes.reactions = {};
  }

  const userId = currentUser.value?.id || 'current_user';
  const reactions = msg.content_attributes.reactions;

  if (reactions[emoji] && reactions[emoji].includes(userId)) {
    reactions[emoji] = reactions[emoji].filter(id => id !== userId);
    if (reactions[emoji].length === 0) {
      delete reactions[emoji];
    }
  } else {
    // Remove reação anterior do usuário
    Object.keys(reactions).forEach(e => {
      reactions[e] = reactions[e].filter(id => id !== userId);
      if (reactions[e].length === 0) {
        delete reactions[e];
      }
    });
    reactions[emoji] = reactions[emoji] || [];
    reactions[emoji].push(userId);
  }
};

const isStickerAttachment = att => {
  const url = att?.data_url || '';
  const ext = att?.extension || '';
  return ext.toLowerCase() === 'webp' || url.includes('.webp') || url.includes('sticker');
};

// Stickers / Figurinhas Methods (Servidor com Compressão 512x512)
const processImageToSticker = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const size = 512;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');

        const scale = Math.min(size / img.width, size / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        const x = (size - w) / 2;
        const y = (size - h) / 2;

        ctx.clearRect(0, 0, size, size);
        ctx.drawImage(img, x, y, w, h);

        const webpDataUrl = canvas.toDataURL('image/webp', 0.85);
        resolve(webpDataUrl);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const fetchStickers = async () => {
  try {
    isLoadingStickers.value = true;
    const response = await crmAPI.getStickers();
    stickers.value = response.data?.stickers || [];
  } catch (err) {
    console.error('Erro ao buscar figurinhas:', err);
  } finally {
    isLoadingStickers.value = false;
  }
};

const toggleStickersMenu = () => {
  showStickersMenu.value = !showStickersMenu.value;
  if (showStickersMenu.value && stickers.value.length === 0) {
    fetchStickers();
  }
};

const triggerStickerFileInput = () => {
  if (stickerFileInputRef.value) {
    stickerFileInputRef.value.click();
  }
};

const onStickerFileSelected = async e => {
  const file = e.target.files && e.target.files[0];
  if (!file) return;

  if (stickers.value.length >= MAX_STICKERS) {
    alert(`Limite de ${MAX_STICKERS} figurinhas atingido. Exclua uma figurinha para adicionar outra.`);
    return;
  }

  isImportingSticker.value = true;
  try {
    const webpUrl = await processImageToSticker(file);
    const res = await crmAPI.createSticker({
      name: file.name.replace(/\.[^/.]+$/, ''),
      image_url: webpUrl,
      file_type: 'image/webp',
    });
    stickers.value.unshift(res.data);
  } catch (err) {
    console.error('Erro ao importar figurinha:', err);
    alert(err.response?.data?.error || 'Erro ao importar figurinha');
  } finally {
    isImportingSticker.value = false;
    if (stickerFileInputRef.value) stickerFileInputRef.value.value = '';
  }
};

const saveStickerFromMessage = async (imageUrl, name = 'Figurinha') => {
  if (stickers.value.length >= MAX_STICKERS) {
    alert(`Limite de ${MAX_STICKERS} figurinhas atingido. Exclua uma figurinha para adicionar outra.`);
    return;
  }

  try {
    const res = await crmAPI.createSticker({
      name,
      image_url: imageUrl,
      file_type: 'image/webp',
    });
    stickers.value.unshift(res.data);
    showStickersMenu.value = true;
  } catch (err) {
    console.error('Erro ao salvar figurinha:', err);
    alert(err.response?.data?.error || 'Erro ao salvar figurinha');
  }
};

const deleteStickerItem = async id => {
  try {
    await crmAPI.deleteSticker(id);
    stickers.value = stickers.value.filter(s => s.id !== id);
  } catch (err) {
    console.error('Erro ao excluir figurinha:', err);
  }
};

const sendStickerItem = async sticker => {
  showStickersMenu.value = false;
  isSending.value = true;
  try {
    const response = await fetch(sticker.image_url);
    const blob = await response.blob();
    const file = new File([blob], `sticker_${Date.now()}.webp`, { type: 'image/webp' });

    await MessageAPI.create({
      conversationId: conversationId.value,
      message: '',
      files: [file],
      private: isPrivateNote.value,
      echo_id: `temp-${Date.now()}`,
    });
    await fetchMessages();
  } catch (err) {
    console.error('Erro ao enviar figurinha:', err);
  } finally {
    isSending.value = false;
  }
};

// File Attachment Handler
const triggerFileInput = () => {
  if (fileInputRef.value) {
    fileInputRef.value.click();
  }
};

const selectedFilePreviewUrl = computed(() => {
  if (!selectedFile.value || selectedFile.value.isVoiceMessage) return null;
  if (selectedFile.value.type?.startsWith('image/')) {
    try {
      return URL.createObjectURL(selectedFile.value);
    } catch {
      return null;
    }
  }
  return null;
});

const onFileSelected = e => {
  const files = e.target.files;
  if (files && files[0]) {
    selectedFile.value = files[0];
  }
};

const handlePaste = e => {
  const clipboardData = e.clipboardData || window.clipboardData;
  if (!clipboardData) return;

  // Verificar se há itens de imagem no clipboard (print da tela / snipping tool)
  const items = Array.from(clipboardData.items || []);
  const imageItem = items.find(item => item.type && item.type.startsWith('image/'));

  if (imageItem) {
    const blob = imageItem.getAsFile();
    if (blob) {
      e.preventDefault();
      const ext = blob.type.split('/')[1] || 'png';
      const file = new File([blob], `print_${Date.now()}.${ext}`, { type: blob.type });
      selectedFile.value = file;
      nextTick(() => {
        if (textareaRef.value) textareaRef.value.focus();
      });
      return;
    }
  }

  // Fallback para arquivos no clipboard
  const files = Array.from(clipboardData.files || []);
  const file = files.find(f => f.size > 0);
  if (file) {
    e.preventDefault();
    selectedFile.value = file;
    nextTick(() => {
      if (textareaRef.value) textareaRef.value.focus();
    });
  }
};

const handleGlobalPaste = e => {
  // Se o drawer estiver aberto e o usuário colar uma imagem fora da textarea
  const target = e.target;
  const isInput = target && ['input', 'select'].includes(target.tagName.toLowerCase());
  if (isInput && target !== textareaRef.value) return;

  handlePaste(e);
};

const removeSelectedFile = () => {
  selectedFile.value = null;
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
};

// Slash Commands
const handleTextareaInput = e => {
  const val = e.target.value;
  const cursorPos = e.target.selectionStart;
  const textBeforeCursor = val.slice(0, cursorPos);
  const lastSlashIndex = textBeforeCursor.lastIndexOf('/');

  if (lastSlashIndex !== -1 && (lastSlashIndex === 0 || /\s/.test(val[lastSlashIndex - 1]))) {
    slashTriggerIndex.value = lastSlashIndex;
    cannedSearchQuery.value = textBeforeCursor.slice(lastSlashIndex + 1);
    showCannedMenu.value = true;
    selectedCannedIndex.value = 0;
  } else {
    showCannedMenu.value = false;
    slashTriggerIndex.value = -1;
  }
};

const handleKeyDown = e => {
  if (showCannedMenu.value && filteredCannedResponses.value.length > 0) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      selectedCannedIndex.value = (selectedCannedIndex.value + 1) % filteredCannedResponses.value.length;
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      selectedCannedIndex.value =
        (selectedCannedIndex.value - 1 + filteredCannedResponses.value.length) %
        filteredCannedResponses.value.length;
      return;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      applyCannedResponse(filteredCannedResponses.value[selectedCannedIndex.value]);
      return;
    }
    if (e.key === 'Escape') {
      showCannedMenu.value = false;
      return;
    }
  }

  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    sendMessage();
  }
};

const applyCannedResponse = canned => {
  if (!canned || slashTriggerIndex.value === -1) return;

  const before = messageContent.value.slice(0, slashTriggerIndex.value);
  const after = messageContent.value.slice(slashTriggerIndex.value + 1 + cannedSearchQuery.value.length);
  messageContent.value = before + canned.content + (after.startsWith(' ') ? after : ` ${after}`);

  showCannedMenu.value = false;
  slashTriggerIndex.value = -1;
  nextTick(() => {
    if (textareaRef.value) {
      textareaRef.value.focus();
    }
  });
};

const onStageChange = async newStageId => {
  if (!props.deal || props.deal.pipeline_stage_id === newStageId) return;

  try {
    const res = await crmAPI.moveDealStage(props.deal.id, {
      pipeline_stage_id: newStageId,
      position: 0,
    });
    emit('deal-updated', res.data);
  } catch (error) {
    console.error('Erro ao atualizar etapa:', error);
  }
};

const startEditValue = () => {
  tempValue.value = Number(props.deal?.value || 0);
  isEditingValue.value = true;
};

const saveValue = async () => {
  if (!props.deal) return;

  try {
    const res = await crmAPI.updateDeal(props.deal.id, {
      value: tempValue.value,
    });
    emit('deal-updated', res.data);
    isEditingValue.value = false;
  } catch (error) {
    console.error('Erro ao atualizar valor:', error);
  }
};

const formatTime = isoDate => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const onNewMessageReceived = data => {
  if (data && Number(data.conversation_id) === Number(conversationId.value)) {
    fetchMessages();
  }
};

// Identificação Inteligente de Paciente / Múltiplos Números / Familiares
const patientInfo = ref({ found: false, patient: null, is_primary: true, relationship: '' });
const isLoadingPatientInfo = ref(false);
const showLinkPatientModal = ref(false);
const allAvailablePatients = ref([]);
const selectedPatientToLink = ref('');
const patientToLinkSearchQuery = ref('');
const isPatientToLinkDropdownOpen = ref(false);
const relationshipToLink = ref('Filha');
const isSubmittingLink = ref(false);

const normalizeText = str => {
  return (str || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
};

const filteredPatientsToLink = computed(() => {
  const query = normalizeText(patientToLinkSearchQuery.value);
  const list = allAvailablePatients.value || [];
  if (!query) return list.slice(0, 20);

  return list.filter(p => {
    const nameNorm = normalizeText(p.name);
    const phoneNorm = (p.phone_number || '').replace(/\D/g, '');
    const queryDigits = query.replace(/\D/g, '');

    const matchesName = nameNorm.includes(query);
    const matchesPhone = queryDigits.length >= 2 && phoneNorm.includes(queryDigits);
    return matchesName || matchesPhone;
  });
});

const selectPatientToLink = p => {
  selectedPatientToLink.value = p.id;
  patientToLinkSearchQuery.value = p.name;
  isPatientToLinkDropdownOpen.value = false;
};

const clearPatientToLinkSelection = () => {
  selectedPatientToLink.value = '';
  patientToLinkSearchQuery.value = '';
  isPatientToLinkDropdownOpen.value = true;
};

const fetchPatientInfo = async () => {
  if (!props.deal?.contact?.id) {
    patientInfo.value = { found: false };
    return;
  }
  isLoadingPatientInfo.value = true;
  try {
    const res = await schedulingAPI.lookupPatientForContact(props.deal.contact.id);
    patientInfo.value = res.data || { found: false };
  } catch (err) {
    patientInfo.value = { found: false };
  } finally {
    isLoadingPatientInfo.value = false;
  }
};

const openLinkPatientModal = async () => {
  showLinkPatientModal.value = true;
  selectedPatientToLink.value = '';
  patientToLinkSearchQuery.value = '';
  isPatientToLinkDropdownOpen.value = false;
  try {
    const res = await schedulingAPI.getPatients();
    allAvailablePatients.value = res.data?.patients || res.data || [];
  } catch (err) {
    console.error('Erro ao listar pacientes:', err);
  }
};

const handleLinkPatient = async () => {
  if (!selectedPatientToLink.value || !props.deal?.contact?.id) return;
  isSubmittingLink.value = true;
  try {
    await schedulingAPI.addSecondaryContact(selectedPatientToLink.value, {
      phone: props.deal.contact.phone_number,
      name: props.deal.contact.name,
      relationship: relationshipToLink.value,
      contact_id: props.deal.contact.id,
    });
    showLinkPatientModal.value = false;
    await fetchPatientInfo();
  } catch (err) {
    alert('Erro ao vincular familiar ao paciente.');
  } finally {
    isSubmittingLink.value = false;
  }
};

const openInInbox = () => {
  if (conversationId.value) {
    window.open(`/app/accounts/${props.accountId}/conversations/${conversationId.value}`, '_blank');
  }
};

// Grid Fetching for Split View
const loadGridData = async () => {
  isLoadingGrid.value = true;
  try {
    const [profsRes, srvsRes] = await Promise.all([
      schedulingAPI.getProfessionals().catch(() => ({ data: [] })),
      schedulingAPI.getServices().catch(() => ({ data: [] })),
    ]);

    gridProfessionals.value = profsRes.data || [];
    gridServices.value = srvsRes.data || [];

    await fetchGrid();
  } catch (err) {
    console.error('Erro ao carregar dados da grade:', err);
  } finally {
    isLoadingGrid.value = false;
  }
};

const fetchGrid = async () => {
  try {
    const params = {};
    if (selectedProfessionalId.value) params.professional_id = selectedProfessionalId.value;
    if (selectedServiceId.value) params.service_id = selectedServiceId.value;

    const res = await schedulingAPI.getFixedGrid(params);
    gridData.value = res.data?.grid || {};
    defaultCapacity.value = res.data?.default_capacity || 1;
  } catch (err) {
    console.error('Erro ao buscar grade:', err);
  }
};

const toggleSplitView = () => {
  isSplitViewOpen.value = !isSplitViewOpen.value;
  if (isSplitViewOpen.value) {
    loadGridData();
  }
};

watch([selectedProfessionalId, selectedServiceId], () => {
  if (isSplitViewOpen.value) {
    fetchGrid();
  }
});

const getSlotInfo = (dayKey, slot) => {
  return (
    gridData.value[dayKey]?.[slot] || {
      capacity: defaultCapacity.value,
      occupants_count: 0,
      free_slots_count: defaultCapacity.value,
      is_full: false,
    }
  );
};

const handleSelectSlot = (dayKey, slot) => {
  const slotInfo = getSlotInfo(dayKey, slot);
  if (slotInfo.is_full) return;

  emit('open-schedule-modal', {
    day_of_week: dayKey,
    day_key: dayKey,
    time_slot: slot,
    professional_id: selectedProfessionalId.value || null,
    service_id: selectedServiceId.value || null,
    deal: props.deal,
  });
};

const handleDirectSchedule = () => {
  emit('open-schedule-modal', {
    professional_id: selectedProfessionalId.value || null,
    service_id: selectedServiceId.value || null,
    deal: props.deal,
  });
};

onMounted(() => {
  store.dispatch('getCannedResponse');
  emitter.on(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, onReplyToMessage);
  document.addEventListener('paste', handleGlobalPaste);
});

onUnmounted(() => {
  cancelAudioRecording();
  stopResize();
  stopChatColumnResize();
  emitter.off(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, onReplyToMessage);
  document.removeEventListener('paste', handleGlobalPaste);
});

watch(
  () => props.deal,
  newDeal => {
    if (newDeal) {
      fetchMessages();
      fetchPatientInfo();
      store.dispatch('getCannedResponse');
      if (isSplitViewOpen.value) {
        loadGridData();
      }
    }
  },
  { immediate: true }
);

useEmitter('message.created', onNewMessageReceived);
useEmitter('message.updated', onNewMessageReceived);
</script>

<template>
  <div v-if="deal" class="fixed inset-0 z-[999] overflow-hidden flex justify-end">
    <!-- Backdrop 100% Escuro e Opaco -->
    <div
      class="fixed inset-0 bg-black/80 backdrop-blur-xs transition-opacity duration-300"
      @click="$emit('close')"
    />

    <!-- Main Container 100% Sólido com Redimensionamento Arrastável & Split View -->
    <div
      class="relative h-full shadow-2xl flex flex-row z-[1000] border-l border-slate-700 overflow-hidden bg-[#111b21]"
      :class="[
        isResizing ? 'select-none' : 'transition-[width] duration-150 ease-out'
      ]"
      :style="{ width: `${currentDrawerWidth}px`, maxWidth: '98vw' }"
    >
      <!-- Resize Handle (Borda Esquerda Arrastável com Cursor col-resize) -->
      <div
        class="absolute left-0 top-0 bottom-0 w-2.5 cursor-col-resize z-50 group flex items-center justify-center hover:bg-emerald-500/40 transition-colors"
        :class="isResizing ? 'bg-emerald-500/60 w-3' : ''"
        title="Arraste para a esquerda ou direita para redimensionar esta janela de conversa"
        @mousedown.prevent="startResize"
      >
        <!-- Linha visual indicadora centralizada -->
        <div
          class="w-1 h-12 bg-slate-600/80 rounded-full group-hover:bg-emerald-400 group-hover:h-20 group-hover:shadow-sm transition-all"
          :class="isResizing ? 'bg-emerald-300 h-24 shadow-md' : ''"
        />
      </div>

      <!-- ============================================== -->
      <!-- COLUNA DA ESQUERDA: CONVERSA ESTILO WHATSAPP   -->
      <!-- ============================================== -->
      <div
        class="flex flex-col h-full bg-[#0b141a] overflow-hidden shrink-0 relative"
        :class="[themeClass, isSplitViewOpen ? '' : 'w-full']"
        :style="isSplitViewOpen ? { width: `${chatColumnWidth}px`, minWidth: '380px' } : {}"
      >
        <!-- Header do Chat -->
        <div class="px-4 py-3 border-b border-slate-800 bg-[#111b21] flex flex-col gap-2.5 shadow-xs shrink-0">
          <div class="flex items-center justify-between">
            <!-- Contact Info -->
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm bg-gradient-to-tr from-emerald-600 to-teal-500 text-white shrink-0 shadow-sm border border-emerald-400/30"
              >
                {{ (contactName || 'L').charAt(0).toUpperCase() }}
              </div>
              <div>
                <div class="flex items-center gap-2">
                  <h2 class="text-sm font-bold text-white tracking-tight">
                    {{ deal.title }}
                  </h2>
                  <span
                    v-if="deal.status === 'won'"
                    class="px-2 py-0.2 text-[10px] font-bold rounded-md bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                  >
                    ✓ Ganho
                  </span>
                  <span
                    v-else-if="deal.status === 'lost'"
                    class="px-2 py-0.2 text-[10px] font-bold rounded-md bg-rose-500/20 text-rose-300 border border-rose-500/30"
                  >
                    Perdido
                  </span>
                </div>
                <div class="flex flex-wrap items-center gap-2 mt-0.5 text-xs text-slate-400">
                  <span class="text-slate-300 font-medium">{{ contactName }}</span>
                  <span v-if="contactIdentifier" class="text-slate-400 font-mono text-[11px]">• {{ contactIdentifier }}</span>

                  <!-- Badge de Paciente Direto -->
                  <span
                    v-if="patientInfo.found && patientInfo.is_primary"
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-950/80 text-emerald-300 border border-emerald-500/30 text-[10px] font-semibold"
                  >
                    🧑‍⚕️ Paciente ({{ patientInfo.patient?.total_sessions_count || 0 }} sessões)
                  </span>

                  <!-- Badge de Familiar / Responsável -->
                  <span
                    v-else-if="patientInfo.found && !patientInfo.is_primary"
                    class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md bg-amber-950/90 text-amber-200 border border-amber-500/40 text-[10px] font-bold shadow-xs"
                  >
                    👨‍👩‍👧 {{ patientInfo.relationship || 'Familiar' }} do paciente <strong class="text-amber-100 ml-0.5">{{ patientInfo.patient?.name }}</strong>
                  </span>

                  <!-- Botão de Vincular a Paciente Existente -->
                  <button
                    v-else
                    type="button"
                    class="inline-flex items-center gap-1 text-[10px] text-cyan-400 hover:text-cyan-300 transition-colors cursor-pointer underline font-medium"
                    title="Vincular este número como familiar/responsável de um paciente existente"
                    @click="openLinkPatientModal"
                  >
                    🔗 Vincular a Paciente
                  </button>
                </div>
              </div>
            </div>

            <!-- Top Action Buttons -->
            <div class="flex items-center gap-1.5">
              <!-- Botão Toggle Lado a Lado / Vagas & Agendar -->
              <button
                type="button"
                class="px-2.5 py-1.5 text-xs font-semibold rounded-xl transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
                :class="
                  isSplitViewOpen
                    ? 'bg-emerald-600 text-white shadow-md ring-2 ring-emerald-500/40'
                    : 'text-emerald-400 hover:text-white bg-emerald-950/60 hover:bg-emerald-600 border border-emerald-500/40'
                "
                :title="isSplitViewOpen ? 'Recolher painel de vagas' : 'Abrir grade de vagas lado a lado com a conversa'"
                @click="toggleSplitView"
              >
                <span>📅</span>
                <span>{{ isSplitViewOpen ? 'Ocultar Vagas' : 'Vagas & Agendar' }}</span>
              </button>

              <!-- Abrir no Inbox -->
              <button
                v-if="hasConversation"
                class="px-2.5 py-1.5 text-xs font-medium text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-xl transition-colors flex items-center gap-1.5 cursor-pointer"
                title="Abrir no Inbox principal"
                @click="openInInbox"
              >
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                <span>Inbox</span>
              </button>

              <!-- Maximizar / Restaurar Largura -->
              <button
                type="button"
                class="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-slate-800 transition-colors cursor-pointer"
                :title="isMaximized ? 'Restaurar largura padrão' : 'Maximizar largura da janela de conversa'"
                @click="toggleMaximize"
              >
                <svg v-if="!isMaximized" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
                <svg v-else class="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 9L4 4m0 0v4m0-4h4m6 5l5-5m0 0v4m0-4h-4m-7 11l-5 5m0 0v-4m0 4h4m11-5l5 5m0 0v-4m0 4h-4" />
                </svg>
              </button>

              <!-- Editar Deal -->
              <button
                class="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-slate-800 transition-colors cursor-pointer"
                title="Editar negócio"
                @click="$emit('edit-deal', deal)"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>

              <!-- Fechar Drawer -->
              <button
                class="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-slate-800 transition-colors cursor-pointer"
                title="Fechar"
                @click="$emit('close')"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Quick Deal Attributes Bar -->
          <div class="flex items-center justify-between gap-3 pt-2 border-t border-slate-800/80">
            <div class="flex items-center gap-2">
              <span class="text-xs text-slate-400 font-medium">Etapa:</span>
              <select
                :value="deal.pipeline_stage_id"
                class="text-xs font-semibold rounded-lg px-2.5 py-1 bg-slate-950 border border-slate-700 text-slate-200 focus:ring-1 focus:ring-emerald-500 focus:outline-none cursor-pointer"
                @change="e => onStageChange(Number(e.target.value))"
              >
                <option v-for="stage in stages" :key="stage.id" :value="stage.id">
                  {{ stage.name }}
                </option>
              </select>
            </div>

            <div class="flex items-center gap-2">
              <span class="text-xs text-slate-400 font-medium">Valor:</span>
              <div v-if="!isEditingValue" class="flex items-center gap-1.5 group cursor-pointer" @click="startEditValue">
                <span class="text-xs font-bold text-emerald-400">
                  {{ formattedValue }}
                </span>
                <svg class="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
              <div v-else class="flex items-center gap-1">
                <input
                  v-model.number="tempValue"
                  type="number"
                  step="0.01"
                  class="w-24 px-2 py-0.5 text-xs bg-slate-950 border border-emerald-500 rounded text-white focus:outline-none"
                  @keyup.enter="saveValue"
                  @keyup.esc="isEditingValue = false"
                />
                <button class="text-emerald-400 hover:text-emerald-300 text-xs font-bold cursor-pointer" @click="saveValue">✓</button>
                <button class="text-slate-400 hover:text-slate-300 text-xs cursor-pointer" @click="isEditingValue = false">✕</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Messages Feed (WhatsApp Wallpaper) -->
        <div
          ref="messagesContainer"
          class="flex-1 overflow-y-auto p-4 space-y-3 bg-[#0b141a] relative"
          style="background-image: radial-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px); background-size: 20px 20px;"
        >
          <!-- Sem conversa vinculada -->
          <div
            v-if="!hasConversation"
            class="h-full flex flex-col items-center justify-center text-center p-6"
          >
            <div class="w-14 h-14 rounded-2xl bg-slate-800/80 border border-slate-700 flex items-center justify-center text-slate-400 mb-3 text-2xl">
              💬
            </div>
            <h3 class="text-sm font-bold text-white mb-1">
              Nenhuma conversa vinculada
            </h3>
            <p class="text-xs text-slate-400 max-w-xs mb-4">
              Este lead ainda não iniciou um chat no WhatsApp ou canal de atendimento.
            </p>
          </div>

          <!-- Loading -->
          <div v-else-if="isLoadingMessages" class="flex justify-center items-center py-10">
            <div class="animate-spin rounded-full h-6 w-6 border-2 border-emerald-500 border-t-transparent"></div>
          </div>

          <!-- Lista de Mensagens -->
          <template v-else>
            <div
              v-if="messages.length === 0"
              class="text-center py-10 text-xs text-slate-500"
            >
              Nenhuma mensagem anterior encontrada.
            </div>

            <template v-for="entry in groupedDisplayMessages" :key="entry.isAlbum ? entry.id : entry.message.id">
              <!-- Álbum Estilo WhatsApp -->
              <div
                v-if="entry.isAlbum"
                class="flex w-full group/msg my-2"
                :class="entry.messageType === 0 ? 'justify-start' : 'justify-end'"
              >
                <div
                  class="flex items-center gap-1.5 max-w-[85%] relative"
                  :class="entry.messageType === 0 ? 'flex-row' : 'flex-row-reverse'"
                >
                  <WhatsappAlbumGrid
                    :items="entry.items"
                    :time="entry.time"
                    :status="entry.status"
                    :is-outgoing="entry.messageType === 1"
                    @contextmenu="openWhatsappContextMenu($event, entry.primaryMessage)"
                  />
                </div>
              </div>

              <!-- Mensagem Normal -->
              <template v-else>
                <div
                  v-for="msg in [entry.message]"
                  :key="msg.id"
                  class="flex w-full group/msg my-1.5"
                  :class="msg.message_type === 0 ? 'justify-start' : 'justify-end'"
                >
                  <div
                    class="flex items-center gap-1.5 max-w-[85%] relative"
                    :class="msg.message_type === 0 ? 'flex-row' : 'flex-row-reverse'"
                  >
                <!-- NOTA PRIVADA -->
                <div
                  v-if="msg.private"
                  class="w-full rounded-2xl px-3.5 py-2 text-xs bg-[#1c242c] border border-amber-500/40 text-amber-200 shadow-md"
                  @contextmenu="openWhatsappContextMenu($event, msg)"
                >
                  <div class="flex items-center gap-1.5 mb-1 text-[10px] font-bold text-amber-400 uppercase tracking-wide">
                    <span>🔒 NOTA INTERNA</span>
                  </div>
                  <p class="whitespace-pre-wrap leading-relaxed text-amber-100">{{ msg.content }}</p>
                  <span class="block text-[9px] text-amber-400/60 text-right mt-1 font-mono">
                    {{ formatTime(msg.created_at) }}
                  </span>
                </div>

                <!-- MENSAGEM RECEBIDA (Cliente / Incoming) -->
                <div
                  v-else-if="msg.message_type === 0"
                  class="rounded-2xl rounded-tl-xs px-3.5 py-2.5 text-xs bg-[#202c33] text-[#e9edef] shadow-md border border-slate-750/30 relative"
                  @contextmenu="openWhatsappContextMenu($event, msg)"
                >
                  <!-- Mensagem Apagada -->
                  <div
                    v-if="isMessageDeleted(msg)"
                    class="flex items-center gap-1.5 text-slate-400 italic text-[12px] py-1 select-none min-w-[140px]"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="size-3.5 opacity-75 shrink-0"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                    </svg>
                    <span>Mensagem apagada</span>
                  </div>

                  <template v-else>
                    <!-- Citação / Resposta Citada dentro do Balão (Estilo WhatsApp) -->
                    <div
                      v-if="getInReplyToMessage(msg)"
                      class="rounded-lg p-2 mb-1.5 flex items-start gap-2 bg-black/30 border-l-[3.5px] border-cyan-400 text-xs select-none"
                    >
                      <div class="min-w-0 flex-1">
                        <span class="font-bold text-[11px] text-cyan-300 block truncate">
                          {{ getInReplyToMessage(msg).sender?.name || (getInReplyToMessage(msg).message_type === 1 ? 'Você' : contactName) }}
                        </span>
                        <span class="text-slate-200/90 text-[11px] line-clamp-1 block truncate">
                          {{ getInReplyToMessage(msg).content || 'Anexo' }}
                        </span>
                      </div>
                    </div>

                    <!-- Áudio Oficial do WhatsApp -->
                    <div v-if="getAudioAttachments(msg).length > 0" class="my-1">
                      <AudioChip
                        v-for="att in getAudioAttachments(msg)"
                        :key="att.id"
                        :attachment="att"
                      />
                    </div>

                    <!-- Imagens / Figurinhas -->
                    <div v-if="getImageAttachments(msg).length > 0" class="mb-1.5 space-y-1">
                      <div
                        v-for="att in getImageAttachments(msg)"
                        :key="att.id"
                        class="relative group/img"
                        :class="isStickerAttachment(att) ? 'w-32 h-32 max-w-[130px] max-h-[130px] flex items-center justify-center' : ''"
                      >
                        <img
                          v-if="att.data_url || att.dataUrl"
                          :src="att.data_url || att.dataUrl"
                          :class="isStickerAttachment(att) ? 'w-full h-full object-contain drop-shadow-md cursor-pointer hover:scale-105 transition-transform' : 'rounded-lg max-h-72 object-cover cursor-pointer hover:opacity-95'"
                          alt="Imagem"
                        />
                        <!-- Botão Salvar em Figurinhas -->
                        <button
                          type="button"
                          class="absolute bottom-2 right-2 opacity-0 group-hover/img:opacity-100 transition-opacity px-2 py-1 bg-black/80 hover:bg-emerald-600 text-white rounded-lg text-[10px] font-bold shadow-md flex items-center gap-1 cursor-pointer"
                          title="Salvar esta imagem nas suas figurinhas favoritas"
                          @click="saveStickerFromMessage(att.data_url || att.dataUrl)"
                        >
                          <span>⭐</span>
                          <span>Salvar Figurinha</span>
                        </button>
                      </div>
                    </div>

                    <!-- Texto -->
                    <p v-if="msg.content" class="whitespace-pre-wrap leading-relaxed select-text">{{ msg.content }}</p>

                    <!-- Hora -->
                    <div class="flex items-center justify-end gap-1 mt-1 text-[10px] text-slate-400 font-mono">
                      <span>{{ formatTime(msg.created_at) }}</span>
                    </div>

                    <!-- Badges de Reações WhatsApp -->
                    <div
                      v-if="msg.content_attributes?.reactions && Object.keys(msg.content_attributes.reactions).length > 0"
                      class="flex flex-wrap items-center gap-1 -mb-4 mt-1.5 justify-start"
                    >
                      <button
                        v-for="(users, emoji) in msg.content_attributes.reactions"
                        :key="emoji"
                        type="button"
                        class="px-2 py-0.5 rounded-full bg-[#182229] border border-slate-700/80 text-[11px] font-bold text-white shadow-md flex items-center gap-1 hover:border-emerald-500 transition-colors cursor-pointer"
                        title="Clique para alternar sua reação"
                        @click="toggleReaction(msg, emoji)"
                      >
                        <span>{{ emoji }}</span>
                        <span v-if="users.length > 1" class="text-[9px] text-slate-400 font-mono">{{ users.length }}</span>
                      </button>
                    </div>
                  </template>
                </div>

                <!-- MENSAGEM ENVIADA (Operador / Outgoing) -->
                <div
                  v-else-if="msg.message_type === 1"
                  class="rounded-2xl rounded-tr-xs px-3.5 py-2.5 text-xs bg-[#005c4b] text-[#e9edef] shadow-md relative"
                  @contextmenu="openWhatsappContextMenu($event, msg)"
                >
                  <!-- Mensagem Apagada -->
                  <div
                    v-if="isMessageDeleted(msg)"
                    class="flex items-center gap-1.5 text-emerald-200/80 italic text-[12px] py-1 select-none min-w-[140px]"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="size-3.5 opacity-75 shrink-0"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                    </svg>
                    <span>Mensagem apagada</span>
                  </div>

                  <template v-else>
                    <!-- Citação / Resposta Citada dentro do Balão (Estilo WhatsApp) -->
                    <div
                      v-if="getInReplyToMessage(msg)"
                      class="rounded-lg p-2 mb-1.5 flex items-start gap-2 bg-black/30 border-l-[3.5px] border-emerald-300 text-xs select-none"
                    >
                      <div class="min-w-0 flex-1">
                        <span class="font-bold text-[11px] text-emerald-300 block truncate">
                          {{ getInReplyToMessage(msg).sender?.name || (getInReplyToMessage(msg).message_type === 1 ? 'Você' : contactName) }}
                        </span>
                        <span class="text-slate-200/90 text-[11px] line-clamp-1 block truncate">
                          {{ getInReplyToMessage(msg).content || 'Anexo' }}
                        </span>
                      </div>
                    </div>

                    <!-- Áudio Oficial do WhatsApp -->
                    <div v-if="getAudioAttachments(msg).length > 0" class="my-1">
                      <AudioChip
                        v-for="att in getAudioAttachments(msg)"
                        :key="att.id"
                        :attachment="att"
                      />
                    </div>

                    <!-- Imagens / Figurinhas -->
                    <div v-if="getImageAttachments(msg).length > 0" class="mb-1.5 space-y-1">
                      <div
                        v-for="att in getImageAttachments(msg)"
                        :key="att.id"
                        class="relative group/img"
                        :class="isStickerAttachment(att) ? 'w-32 h-32 max-w-[130px] max-h-[130px] flex items-center justify-center' : ''"
                      >
                        <img
                          v-if="att.data_url || att.dataUrl"
                          :src="att.data_url || att.dataUrl"
                          :class="isStickerAttachment(att) ? 'w-full h-full object-contain drop-shadow-md cursor-pointer hover:scale-105 transition-transform' : 'rounded-lg max-h-72 object-cover cursor-pointer hover:opacity-95'"
                          alt="Imagem"
                        />
                        <!-- Botão Salvar em Figurinhas -->
                        <button
                          type="button"
                          class="absolute bottom-2 right-2 opacity-0 group-hover/img:opacity-100 transition-opacity px-2 py-1 bg-black/80 hover:bg-emerald-600 text-white rounded-lg text-[10px] font-bold shadow-md flex items-center gap-1 cursor-pointer"
                          title="Salvar esta imagem nas suas figurinhas favoritas"
                          @click="saveStickerFromMessage(att.data_url || att.dataUrl)"
                        >
                          <span>⭐</span>
                          <span>Salvar Figurinha</span>
                        </button>
                      </div>
                    </div>

                    <!-- Texto -->
                    <p v-if="msg.content" class="whitespace-pre-wrap leading-relaxed select-text">{{ msg.content }}</p>

                    <!-- Hora + Duplo Check Azul (✓✓) -->
                    <div class="flex items-center justify-end gap-1 mt-1 text-[10px] text-emerald-200/70 font-mono">
                      <span>{{ formatTime(msg.created_at) }}</span>
                      <span class="text-sky-300 font-bold text-[11px]" title="Entregue e visualizado">✓✓</span>
                    </div>

                    <!-- Badges de Reações WhatsApp -->
                    <div
                      v-if="msg.content_attributes?.reactions && Object.keys(msg.content_attributes.reactions).length > 0"
                      class="flex flex-wrap items-center gap-1 -mb-4 mt-1.5 justify-end"
                    >
                      <button
                        v-for="(users, emoji) in msg.content_attributes.reactions"
                        :key="emoji"
                        type="button"
                        class="px-2 py-0.5 rounded-full bg-[#182229] border border-slate-700/80 text-[11px] font-bold text-white shadow-md flex items-center gap-1 hover:border-emerald-500 transition-colors cursor-pointer"
                        title="Clique para alternar sua reação"
                        @click="toggleReaction(msg, emoji)"
                      >
                        <span>{{ emoji }}</span>
                        <span v-if="users.length > 1" class="text-[9px] text-slate-400 font-mono">{{ users.length }}</span>
                      </button>
                    </div>
                  </template>
                </div>

                <!-- Botões Flutuantes de Ação (Responder + Reagir) -->
                <div
                  v-if="!msg.private && !isMessageDeleted(msg)"
                  class="relative shrink-0 flex items-center gap-1 opacity-0 group-hover/msg:opacity-100 transition-opacity"
                >
                  <!-- Botão Responder (↩️) -->
                  <button
                    type="button"
                    class="p-1.5 rounded-full bg-[#182229] hover:bg-slate-700 text-slate-300 hover:text-white shadow-lg border border-slate-700/80 cursor-pointer transition-transform hover:scale-110 flex items-center justify-center"
                    title="Responder a esta mensagem"
                    @click.stop="onReplyToMessage(msg)"
                  >
                    <svg class="size-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M3 10h10a5 5 0 015 5v3M3 10l6-6m-6 6l6 6" />
                    </svg>
                  </button>

                  <!-- Botão Reagir (😊) -->
                  <button
                    type="button"
                    class="p-1.5 rounded-full bg-[#182229] hover:bg-slate-700 text-slate-300 hover:text-white shadow-lg border border-slate-700/80 cursor-pointer transition-transform hover:scale-110 flex items-center justify-center"
                    title="Reagir à mensagem"
                    @click.stop="toggleReactionMenu(msg.id)"
                  >
                    <span class="text-xs">😊</span>
                  </button>

                  <!-- Mini Menu Flutuante de Emojis Rápidos -->
                  <div
                    v-if="activeReactionMenuMsgId === msg.id"
                    class="absolute -top-9 z-30 flex items-center gap-1 bg-[#233138] border border-slate-700 px-2.5 py-1 rounded-full shadow-2xl animate-fade-in"
                    :class="msg.message_type === 0 ? 'left-0' : 'right-0'"
                  >
                    <button
                      v-for="emoji in QUICK_REACTIONS"
                      :key="emoji"
                      type="button"
                      class="text-sm hover:scale-130 transition-transform p-0.5 cursor-pointer"
                      @click.stop="toggleReaction(msg, emoji)"
                    >
                      {{ emoji }}
                    </button>
                  </div>
                </div>
                </div>
              </div>
            </template>
          </template>
        </template>
      </div>

        <!-- Composer Footer -->
        <div
          v-if="hasConversation"
          class="relative p-3 border-t border-slate-800 bg-[#202c33] shrink-0"
        >
          <!-- Popover de Figurinhas Favoritas (Máx 20) -->
          <div
            v-if="showStickersMenu"
            class="absolute bottom-full left-3 right-3 mb-2 bg-[#182229] border border-slate-700 rounded-2xl shadow-2xl overflow-hidden z-50 animate-fade-in flex flex-col max-h-80"
          >
            <!-- Header da Galeria -->
            <div class="px-4 py-2.5 border-b border-slate-800 bg-[#111b21] flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="text-base">🎴</span>
                <h4 class="text-xs font-bold text-white">Figurinhas da Clínica</h4>
                <span class="text-[10px] font-mono px-2 py-0.5 rounded-full bg-slate-800 text-emerald-400 border border-emerald-500/30">
                  {{ stickers.length }} / {{ MAX_STICKERS }} salvas
                </span>
              </div>

              <div class="flex items-center gap-2">
                <input
                  ref="stickerFileInputRef"
                  type="file"
                  class="hidden"
                  accept="image/webp,image/png,image/jpeg,image/gif"
                  @change="onStickerFileSelected"
                />
                <button
                  type="button"
                  :disabled="stickers.length >= MAX_STICKERS || isImportingSticker"
                  class="px-2.5 py-1 text-[11px] font-bold bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-lg transition-all shadow-xs flex items-center gap-1 cursor-pointer"
                  @click="triggerStickerFileInput"
                >
                  <span v-if="isImportingSticker" class="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span v-else>+ Importar do PC</span>
                </button>
                <button
                  type="button"
                  class="p-1 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 cursor-pointer"
                  @click="showStickersMenu = false"
                >
                  ✕
                </button>
              </div>
            </div>

            <!-- Grid de Figurinhas -->
            <div class="p-3 overflow-y-auto flex-1">
              <div v-if="isLoadingStickers" class="flex justify-center items-center py-10">
                <div class="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              </div>

              <div v-else-if="stickers.length === 0" class="text-center py-8 text-xs text-slate-400 space-y-1">
                <p>Nenhuma figurinha salva ainda.</p>
                <p class="text-[11px] text-slate-500">Clique em "+ Importar do PC" ou salve fotos da conversa!</p>
              </div>

              <div v-else class="grid grid-cols-4 sm:grid-cols-5 gap-3">
                <div
                  v-for="stk in stickers"
                  :key="stk.id"
                  class="relative group/stk p-1 rounded-xl bg-[#111b21] hover:bg-emerald-950/40 border border-slate-800 hover:border-emerald-500 transition-all flex items-center justify-center cursor-pointer"
                  :title="`Enviar figurinha '${stk.name || 'Figurinha'}'`"
                  @click="sendStickerItem(stk)"
                >
                  <img
                    :src="stk.image_url"
                    class="w-16 h-16 object-contain rounded-lg transition-transform group-hover/stk:scale-105"
                    :alt="stk.name"
                  />
                  <button
                    type="button"
                    class="absolute -top-1.5 -right-1.5 opacity-0 group-hover/stk:opacity-100 transition-opacity p-1 bg-rose-600 hover:bg-rose-500 text-white rounded-full text-[9px] shadow-md cursor-pointer"
                    title="Excluir figurinha"
                    @click.stop="deleteStickerItem(stk.id)"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Floating Canned Menu -->
          <div
            v-if="showCannedMenu && filteredCannedResponses.length > 0"
            class="absolute bottom-full left-3 right-3 mb-2 bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden z-50 animate-fade-in max-h-64 flex flex-col"
          >
            <div class="px-3.5 py-2 border-b border-slate-800 bg-slate-950/80 flex items-center justify-between">
              <div class="flex items-center gap-1.5 text-xs font-bold text-emerald-400">
                <span class="px-1.5 py-0.5 rounded bg-emerald-600/30 font-mono text-[11px]">/</span>
                <span>Respostas Rápidas</span>
              </div>
              <span class="text-[10px] text-slate-500">
                Use ↑ ↓ para navegar e Enter para inserir
              </span>
            </div>

            <div class="overflow-y-auto divide-y divide-slate-800/60 p-1">
              <div
                v-for="(canned, idx) in filteredCannedResponses"
                :key="canned.id"
                class="p-2.5 rounded-xl cursor-pointer transition-all flex flex-col gap-1 select-none"
                :class="[
                  selectedCannedIndex === idx
                    ? 'bg-emerald-600/20 border border-emerald-500/40 text-white'
                    : 'hover:bg-slate-800/60 text-slate-300'
                ]"
                @click="applyCannedResponse(canned)"
              >
                <div class="flex items-center justify-between">
                  <span class="font-mono text-xs font-bold text-emerald-400">/{{ canned.short_code }}</span>
                </div>
                <p class="text-xs text-slate-400 line-clamp-2">{{ canned.content }}</p>
              </div>
            </div>
          </div>

          <!-- Mode Toggle Tabs -->
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-1 bg-[#111b21] p-1 rounded-xl border border-slate-800">
              <button
                type="button"
                class="px-3 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5"
                :class="!isPrivateNote ? 'bg-emerald-600 text-white shadow-xs' : 'text-slate-400 hover:text-slate-200'"
                @click="isPrivateNote = false"
              >
                <span>💬 Mensagem</span>
              </button>
              <button
                type="button"
                class="px-3 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5"
                :class="isPrivateNote ? 'bg-amber-600 text-white shadow-xs' : 'text-slate-400 hover:text-slate-200'"
                @click="isPrivateNote = true"
              >
                <span>🔒 Nota Privada</span>
              </button>
            </div>

            <div class="flex items-center gap-2 text-xs text-slate-400">
              <span class="text-[11px]">Digite <strong class="text-emerald-400 font-mono">/</strong> para respostas rápidas</span>
            </div>
          </div>

          <!-- Banner de Citação / Resposta (Reply To) -->
          <div
            v-if="inReplyToMessage"
            class="mb-2 p-2 rounded-xl bg-[#111b21] border-l-4 border-emerald-500 flex items-center justify-between text-xs animate-fade-in"
          >
            <div class="truncate">
              <span class="font-bold text-emerald-400 block text-[10px]">
                Respondendo a {{ inReplyToMessage.sender?.name || 'Mensagem' }}
              </span>
              <span class="text-slate-300 truncate block text-[11px]">
                {{ inReplyToMessage.content || 'Anexo' }}
              </span>
            </div>
            <button
              type="button"
              class="text-slate-400 hover:text-white p-1 text-xs cursor-pointer font-bold ml-2"
              title="Cancelar resposta"
              @click="cancelReply"
            >
              ✕
            </button>
          </div>

          <!-- Preview de Arquivo / Imagem Colada (Print) -->
          <div v-if="selectedFile && !selectedFile.isVoiceMessage" class="mb-2 p-2 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-between text-xs animate-fade-in">
            <div class="flex items-center gap-2.5 text-emerald-300 truncate">
              <!-- Miniatura se for imagem / print -->
              <img
                v-if="selectedFilePreviewUrl"
                :src="selectedFilePreviewUrl"
                class="w-11 h-11 object-cover rounded-lg border border-slate-700 shrink-0 shadow-md"
                alt="Prévia do print"
              />
              <span v-else class="text-base">📎</span>
              <div class="truncate">
                <span class="truncate font-semibold block text-white">{{ selectedFile.name || 'print.png' }}</span>
                <span class="text-[10px] text-slate-400 block font-mono">{{ (selectedFile.size / 1024).toFixed(1) }} KB</span>
              </div>
            </div>
            <button type="button" class="text-rose-400 hover:text-rose-300 text-xs font-bold px-2.5 py-1 rounded-lg hover:bg-rose-950/40 cursor-pointer transition-colors" @click="removeSelectedFile">
              ✕ Remover
            </button>
          </div>

          <!-- BARRA DE ENTRADA -->
          <div class="flex items-center gap-2">
            <input
              ref="fileInputRef"
              type="file"
              class="hidden"
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
              @change="onFileSelected"
            />

            <!-- Clipe Anexo -->
            <button
              type="button"
              class="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-slate-800 transition-colors cursor-pointer"
              title="Anexar foto ou documento"
              @click="triggerFileInput"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
            </button>

            <!-- Botão de Figurinhas -->
            <button
              type="button"
              class="p-2 text-slate-400 hover:text-amber-400 rounded-xl hover:bg-slate-800 transition-colors cursor-pointer text-base"
              :class="showStickersMenu ? 'text-amber-400 bg-slate-800' : ''"
              title="Figurinhas Favoritas da Clínica"
              @click="toggleStickersMenu"
            >
              🎴
            </button>

            <!-- GRAVANDO ÁUDIO OFICIAL (Zero Delay + Ondas em Tempo Real Compactas) -->
            <div
              v-if="isRecordingAudio"
              class="flex-1 flex items-center gap-2 bg-[#202c33] border border-emerald-500/40 rounded-2xl px-3 py-1.5 text-xs shadow-inner animate-fade-in"
            >
              <!-- Ícone Mic Gravando / Pulsando -->
              <div class="flex items-center gap-1.5 text-rose-400 font-bold shrink-0">
                <span class="size-2 rounded-full bg-rose-500 animate-ping inline-block" />
                <span class="text-[11px] font-mono text-slate-300">{{ recordingAudioDurationText || '00:00' }}</span>
              </div>

              <!-- Ondas do Áudio WaveSurfer Compactas -->
              <div class="flex-1 overflow-hidden min-w-0">
                <AudioRecorder
                  ref="audioRecorderInput"
                  audio-record-format="ogg"
                  @finish-record="onFinishRecorder"
                  @recorder-progress-changed="onAudioProgressChanged"
                  @record-error="onRecordError"
                  @play="recordingAudioState = 'playing'"
                  @pause="recordingAudioState = 'paused'"
                />
              </div>

              <!-- Ações da Gravação -->
              <div class="flex items-center gap-1 shrink-0">
                <!-- Botão Pausar / Ouvir Prévia -->
                <button
                  type="button"
                  class="p-1.5 rounded-lg text-slate-300 hover:text-white bg-slate-700/60 hover:bg-slate-700 transition-colors cursor-pointer text-xs"
                  :title="recordingAudioState === 'playing' ? 'Pausar áudio' : 'Ouvir prévia'"
                  @click="toggleAudioRecorderPlayPause"
                >
                  <span>{{ recordingAudioState === 'playing' ? '⏸️' : '▶️' }}</span>
                </button>

                <!-- Cancelar (Lixeira) -->
                <button
                  type="button"
                  class="p-1.5 rounded-lg text-rose-400 hover:text-rose-200 hover:bg-rose-900/40 transition-colors cursor-pointer"
                  title="Cancelar e descartar áudio"
                  @click="cancelAudioRecording"
                >
                  <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>

                <!-- Enviar Áudio (Aviãozinho / Botão Verde Redondo) -->
                <button
                  type="button"
                  class="p-2 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold transition-transform hover:scale-105 shadow-md cursor-pointer flex items-center justify-center"
                  title="Enviar áudio"
                  :disabled="isSending"
                  @click="sendRecordedAudio"
                >
                  <svg class="size-4 transform rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </div>
            </div>

            <!-- DIGITAÇÃO NORMAL -->
            <template v-else>
              <textarea
                ref="textareaRef"
                v-model="messageContent"
                rows="1"
                :placeholder="
                  isPrivateNote
                    ? 'Escreva uma nota interna privada...'
                    : 'Digite uma mensagem... (digite / para respostas rápidas)'
                "
                class="flex-1 resize-none rounded-2xl px-4 py-2.5 text-xs bg-[#2a3942] border border-transparent focus:border-emerald-500/50 text-white focus:outline-none leading-relaxed transition-all placeholder:text-slate-400"
                :class="isPrivateNote ? 'bg-[#1c242c] border-amber-500/40 text-amber-200 placeholder:text-amber-500/60' : ''"
                @input="handleTextareaInput"
                @keydown="handleKeyDown"
                @paste="handlePaste"
              />

              <!-- MICROFONE (Vazio) -->
              <button
                v-if="!messageContent.trim() && !selectedFile"
                type="button"
                class="p-2.5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white transition-all shadow-md cursor-pointer hover:scale-105"
                title="Gravar áudio"
                @click="toggleAudioRecorder"
              >
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                  <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                </svg>
              </button>

              <!-- BOTÃO ENVIAR (Com Conteúdo) -->
              <button
                v-else
                type="button"
                :disabled="isSending"
                class="p-2.5 rounded-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white transition-all shadow-md cursor-pointer hover:scale-105 flex items-center justify-center"
                title="Enviar mensagem"
                @click="sendMessage"
              >
                <span v-if="isSending" class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <svg v-else class="w-4 h-4 transform rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </template>
          </div>
        </div>
      </div>

      <!-- Divisor Interno Arrastável (Splitter Handle entre Chat e Grade) -->
      <div
        v-if="isSplitViewOpen"
        class="relative w-1.5 bg-slate-800 hover:bg-emerald-500/60 cursor-col-resize z-40 shrink-0 group flex items-center justify-center transition-colors select-none"
        :class="isResizingChatColumn ? 'bg-emerald-500 w-2' : ''"
        title="Arraste para a esquerda ou direita para ajustar a divisão entre a conversa e as vagas"
        @mousedown.prevent="startChatColumnResize"
      >
        <div
          class="w-0.5 h-12 bg-slate-600 rounded-full group-hover:bg-white group-hover:h-20 transition-all"
          :class="isResizingChatColumn ? 'bg-white h-24 shadow-md' : ''"
        />
      </div>

      <!-- ============================================== -->
      <!-- COLUNA DA DIREITA: GRADE DE VAGAS SEMANAL      -->
      <!-- ============================================== -->
      <div
        v-if="isSplitViewOpen"
        class="flex-1 flex flex-col h-full bg-[#111b21] min-w-0 overflow-hidden animate-fade-in"
      >
        <!-- Header da Coluna de Vagas -->
        <div class="px-4 py-2.5 border-b border-slate-800 bg-[#182229] flex items-center justify-between shadow-xs shrink-0 gap-2">
          <div class="flex items-center gap-2 min-w-0">
            <div class="w-7 h-7 rounded-lg bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 font-bold text-sm shrink-0">
              📅
            </div>
            <div class="min-w-0 truncate">
              <h3 class="text-xs font-bold text-white truncate">
                Vagas & Horários em Tempo Real
              </h3>
              <p class="text-[10px] text-slate-400 truncate">Clique em um horário para agendar com este lead</p>
            </div>
          </div>

          <div class="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              class="px-2.5 py-1 text-xs font-bold bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-all shadow-xs cursor-pointer whitespace-nowrap"
              @click="handleDirectSchedule"
            >
              + Novo Agendamento
            </button>
            <button
              type="button"
              class="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors cursor-pointer"
              title="Fechar painel de vagas"
              @click="isSplitViewOpen = false"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Filtros & Turnos -->
        <div class="p-2.5 border-b border-slate-800 bg-[#111b21] flex flex-wrap items-center justify-between gap-2 shrink-0">
          <!-- Switcher de Turnos -->
          <div class="flex items-center gap-1 bg-[#0b141a] p-1 rounded-xl border border-slate-800">
            <button
              v-for="p in periods"
              :key="p.key"
              type="button"
              class="px-2 py-0.5 rounded-lg text-[11px] font-semibold transition-all cursor-pointer flex items-center gap-1"
              :class="
                selectedPeriod === p.key
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'text-slate-400 hover:text-slate-200'
              "
              @click="selectedPeriod = p.key"
            >
              <span>{{ p.icon }}</span>
              <span>{{ p.label }}</span>
            </button>
          </div>

          <!-- Dropdowns de Profissional e Serviço -->
          <div class="flex items-center gap-2">
            <select
              v-model="selectedProfessionalId"
              class="text-xs bg-[#0b141a] border border-slate-800 rounded-xl px-2.5 py-1 text-slate-200 focus:outline-none focus:border-emerald-500 cursor-pointer"
            >
              <option value="">Todos os Profissionais</option>
              <option v-for="prof in gridProfessionals" :key="prof.id" :value="prof.id">
                {{ prof.name }}
              </option>
            </select>

            <select
              v-model="selectedServiceId"
              class="text-xs bg-[#0b141a] border border-slate-800 rounded-xl px-2.5 py-1 text-slate-200 focus:outline-none focus:border-emerald-500 cursor-pointer"
            >
              <option value="">Todos os Serviços</option>
              <option v-for="srv in gridServices" :key="srv.id" :value="srv.id">
                {{ srv.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Grade de Vagas Semanal -->
        <div class="flex-1 overflow-y-auto overflow-x-auto p-3 bg-[#0b141a]">
          <div v-if="isLoadingGrid" class="flex flex-col items-center justify-center py-20 text-slate-400 space-y-2">
            <div class="w-7 h-7 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            <span class="text-xs">Carregando disponibilidade...</span>
          </div>

          <div v-else class="border border-slate-800 rounded-xl overflow-hidden bg-[#111b21] shadow-sm min-w-[520px]">
            <!-- Cabeçalho dos Dias -->
            <div class="grid grid-cols-7 border-b border-slate-800 bg-[#182229] text-center font-bold text-slate-400 py-2">
              <div class="text-slate-500 font-mono text-[10px] uppercase">Horário</div>
              <div v-for="d in daysOfWeek" :key="d.key" class="text-slate-200 text-xs font-bold">
                {{ d.label }}
              </div>
            </div>

            <!-- Linhas de Horário -->
            <div
              v-for="slot in timeSlots"
              :key="slot"
              class="grid grid-cols-7 border-b border-slate-800/80 last:border-b-0 hover:bg-slate-900/60 transition-colors"
            >
              <!-- Rótulo do Horário -->
              <div class="p-1 border-r border-slate-800/80 font-mono text-[11px] font-semibold text-slate-300 flex items-center justify-center bg-[#182229]/60">
                {{ slot }}
              </div>

              <!-- Células dos Dias -->
              <div
                v-for="d in daysOfWeek"
                :key="`${d.key}-${slot}`"
                class="p-1 border-r border-slate-800/80 last:border-r-0 flex items-center justify-center"
              >
                <!-- Horário Lotado -->
                <div
                  v-if="getSlotInfo(d.key, slot).is_full"
                  class="w-full h-8 px-1 rounded-lg text-center bg-rose-950/40 border border-rose-900/50 text-rose-300 text-[10px] font-bold cursor-not-allowed flex items-center justify-center"
                  title="Horário lotado / sem vagas"
                >
                  Lotado
                </div>

                <!-- Horário com Vagas Parciais (Turma em andamento) -->
                <div
                  v-else-if="getSlotInfo(d.key, slot).occupants_count > 0"
                  class="w-full h-8 px-1 rounded-lg text-center bg-emerald-950/40 border border-emerald-800/50 hover:border-emerald-400 hover:bg-emerald-900/50 text-emerald-300 text-[10px] font-bold cursor-pointer transition-all flex flex-col items-center justify-center group"
                  title="Clique para agendar neste horário"
                  @click="handleSelectSlot(d.key, slot)"
                >
                  <span class="text-[10px] text-emerald-300 font-semibold leading-tight">{{ getSlotInfo(d.key, slot).free_slots_count }} livre(s)</span>
                  <span class="text-[9px] text-slate-400 group-hover:text-emerald-200">({{ getSlotInfo(d.key, slot).occupants_count }}/{{ getSlotInfo(d.key, slot).capacity }})</span>
                </div>

                <!-- Horário 100% Livre (Clean Canvas com Hover Interativo) -->
                <div
                  v-else
                  class="w-full h-8 rounded-lg flex items-center justify-center cursor-pointer transition-all duration-150 border border-transparent hover:border-emerald-500/50 hover:bg-emerald-950/40 group"
                  title="Horário 100% livre! Clique para agendar para este lead"
                  @click="handleSelectSlot(d.key, slot)"
                >
                  <span class="text-slate-700 text-sm group-hover:hidden select-none">·</span>
                  <span class="hidden group-hover:inline-flex items-center gap-0.5 text-emerald-400 font-bold text-[10px] animate-fade-in">
                    <span>+</span>
                    <span>Agendar</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Rodapé da Grade -->
        <div class="px-4 py-2 border-t border-slate-800 bg-[#182229] flex items-center justify-between text-[11px] text-slate-400 shrink-0">
          <div class="flex items-center gap-3">
            <span class="flex items-center gap-1"><span class="w-2 h-2 rounded-full bg-emerald-400"></span> Disponível</span>
            <span class="flex items-center gap-1"><span class="w-2 h-2 rounded-full bg-rose-400"></span> Lotado</span>
          </div>
          <span class="text-slate-500">Clique em qualquer vaga para agendar para este lead</span>
        </div>
      </div>
    </div>

    <!-- Modal: Vincular Contato/Familiar a Paciente Existente -->
    <div
      v-if="showLinkPatientModal"
      class="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-slate-900/50 dark:bg-black/80 backdrop-blur-xs"
    >
      <div class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4">
        <div class="flex items-center justify-between">
          <h3 class="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <span>🔗</span>
            <span>Vincular Familiar ao Paciente</span>
          </h3>
          <button
            type="button"
            class="text-slate-400 hover:text-slate-600 dark:hover:text-white text-lg p-1 cursor-pointer"
            @click="showLinkPatientModal = false"
          >
            ✕
          </button>
        </div>

        <p class="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
          Associe o contato atual (<strong class="text-slate-900 dark:text-white">{{ contactName }} - {{ contactIdentifier }}</strong>) a um paciente existente na clínica (ex: pai, mãe, filho).
        </p>

        <div class="space-y-3">
          <div class="relative">
            <label class="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1">
              Buscar Paciente da Clínica (Nome ou WhatsApp)
            </label>
            <div class="relative">
              <input
                v-model="patientToLinkSearchQuery"
                type="text"
                class="w-full h-10 pl-9 pr-8 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                placeholder="Digite o nome ou telefone do paciente..."
                @focus="isPatientToLinkDropdownOpen = true"
                @input="isPatientToLinkDropdownOpen = true"
              />
              <span class="absolute left-3 top-2.5 text-slate-400 text-xs">🔍</span>
              <button
                v-if="patientToLinkSearchQuery || selectedPatientToLink"
                type="button"
                class="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600 dark:hover:text-white text-xs cursor-pointer p-0.5"
                title="Limpar seleção"
                @click="clearPatientToLinkSelection"
              >
                ✕
              </button>
            </div>

            <!-- Dropdown Flutuante de Pacientes Encontrados -->
            <div
              v-if="isPatientToLinkDropdownOpen"
              class="absolute left-0 right-0 top-full mt-1 max-h-48 overflow-y-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl z-50 divide-y divide-slate-100 dark:divide-slate-700/50"
            >
              <div
                v-if="filteredPatientsToLink.length === 0"
                class="px-3 py-3 text-center text-xs text-slate-500 dark:text-slate-400"
              >
                Nenhum paciente encontrado com esse nome.
              </div>
              <button
                v-for="p in filteredPatientsToLink"
                :key="p.id"
                type="button"
                class="w-full px-3 py-2.5 text-left text-xs flex items-center justify-between hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                :class="selectedPatientToLink === p.id ? 'bg-cyan-50 dark:bg-cyan-950/40 text-cyan-700 dark:text-cyan-300 font-bold' : 'text-slate-700 dark:text-slate-200'"
                @click="selectPatientToLink(p)"
              >
                <div class="flex flex-col min-w-0 pr-2">
                  <span class="truncate">{{ p.name }}</span>
                  <span class="text-[10px] text-slate-500 dark:text-slate-400 font-mono">{{ p.phone_number || 'Sem telefone' }}</span>
                </div>
                <span v-if="selectedPatientToLink === p.id" class="text-cyan-600 dark:text-cyan-400 font-bold shrink-0">✓ Selecionado</span>
              </button>
            </div>
          </div>

          <div>
            <label class="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1">
              Grau de Parentesco / Relação
            </label>
            <select
              v-model="relationshipToLink"
              class="w-full h-10 px-3 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white focus:outline-none focus:border-cyan-500 cursor-pointer"
            >
              <option value="Filha">Filha</option>
              <option value="Filho">Filho</option>
              <option value="Mãe">Mãe</option>
              <option value="Pai">Pai</option>
              <option value="Esposa">Esposa</option>
              <option value="Esposo">Esposo</option>
              <option value="Responsável Legal">Responsável Legal</option>
              <option value="Secretária / Cuidador">Secretária / Cuidador</option>
              <option value="Outro">Outro Familiar</option>
            </select>
          </div>
        </div>

        <div class="flex items-center justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-800">
          <button
            type="button"
            class="px-4 py-2 text-xs font-semibold text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-xl transition-colors cursor-pointer"
            @click="showLinkPatientModal = false"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="px-4 py-2 text-xs font-bold text-white bg-cyan-600 hover:bg-cyan-500 rounded-xl transition-colors flex items-center gap-1.5 shadow-sm disabled:opacity-50 cursor-pointer"
            :disabled="!selectedPatientToLink || isSubmittingLink"
            @click="handleLinkPatient"
          >
            <span>{{ isSubmittingLink ? 'Vinculando...' : 'Confirmar Vínculo' }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
